import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { MessageBox } from '../components/ui/MessageBox';
import type { MessageBoxMode } from '../components/ui/MessageBoxMode';
import type { MessageBoxContextValue } from './MessageBoxContextValue';
import type { MessageBoxOptions } from './MessageBoxOptions';

type PendingMessageBox = MessageBoxOptions & {
  mode: MessageBoxMode;
  resolve: (value: boolean) => void;
};

const MessageBoxContext = createContext<MessageBoxContextValue | null>(null);

function normalizeOptions(options: MessageBoxOptions | string): MessageBoxOptions {
  return typeof options === 'string' ? { message: options } : options;
}

export function MessageBoxProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingMessageBox | null>(null);

  const open = useCallback((mode: MessageBoxMode, options: MessageBoxOptions | string): Promise<boolean> => {
    const normalized = normalizeOptions(options);

    return new Promise<boolean>((resolve) => {
      setPending({ ...normalized, mode, resolve });
    });
  }, []);

  const confirm = useCallback(
    (options: MessageBoxOptions | string): Promise<boolean> => open('confirm', options),
    [open],
  );

  const alert = useCallback(async (options: MessageBoxOptions | string): Promise<void> => {
    await open('alert', options);
  }, [open]);

  const confirmDelete = useCallback(
    (options: MessageBoxOptions | string): Promise<boolean> => {
      const normalized = normalizeOptions(options);
      return open('confirm', {
        title: normalized.title ?? 'Excluir',
        confirmLabel: normalized.confirmLabel ?? 'Excluir',
        cancelLabel: normalized.cancelLabel ?? 'Cancelar',
        tone: normalized.tone ?? 'danger',
        message: normalized.message,
        details: normalized.details,
      });
    },
    [open],
  );

  const close = useCallback((result: boolean) => {
    setPending((current) => {
      current?.resolve(result);
      return null;
    });
  }, []);

  const value = useMemo(
    () => ({ confirm, alert, confirmDelete }),
    [confirm, alert, confirmDelete],
  );

  return (
    <MessageBoxContext.Provider value={value}>
      {children}
      {pending ? (
        <MessageBox
          open
          mode={pending.mode}
          title={pending.title}
          message={pending.message}
          details={pending.details}
          confirmLabel={pending.confirmLabel}
          cancelLabel={pending.cancelLabel}
          tone={pending.tone}
          onConfirm={() => close(true)}
          onCancel={() => close(false)}
        />
      ) : null}
    </MessageBoxContext.Provider>
  );
}

export function useMessageBox(): MessageBoxContextValue {
  const context = useContext(MessageBoxContext);
  if (!context) {
    throw new Error('useMessageBox deve ser usado dentro de MessageBoxProvider.');
  }

  return context;
}
